import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/feature/Layout';
import StickerSitesCountBadge from '../../components/feature/StickerSitesCountBadge';
import { fetchFeaturedMaterial } from '../../repositories/materials';
import { fetchRecentQuestions } from '../../repositories/questions';
import { fetchRecentVoices } from '../../repositories/voices';
import type { Material } from '../../types/materials';
import type { Question } from '../../types/questions';
import type { Voice } from '../../types/voices';
import { SUBJECT_ICON_MAP, getGradeRangeLabel } from '../../constants/subjects';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

function HomePage() {
  const [featuredMaterial, setFeaturedMaterial] = useState<Material | null>(null);
  const [recentQuestions, setRecentQuestions] = useState<Question[]>([]);
  const [recentVoices, setRecentVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [voicesLoading, setVoicesLoading] = useState(true);
  const [questionsError, setQuestionsError] = useState(false);
  const [voicesError, setVoicesError] = useState(false);

  // SEO設定
  useSEO({
    title: '中学教材まとめ｜無料で使える教材・単元ガイド｜キョウコレ',
    description: '中学生向けの無料教材を、教科別・単元別にまとめて探せます。学習支援・こどもの居場所のための教材情報プラットフォーム。単元ガイド、学習相談、居場所の悩み共有など、学びをサポートする情報が満載です。',
    keywords: '中学 教材 無料,中学 教材 まとめ,学習支援,教材,こどもの居場所,単元ガイド,学習相談,教材サイト,活用レシピ',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          '中学教材まとめ｜無料で使える教材・単元ガイド｜キョウコレ',
          '中学生向けの無料教材を、教科別・単元別にまとめて探せます。学習支援・こどもの居場所のための教材情報プラットフォーム。',
          '/'
        ),
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'ホーム',
              item: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/',
            },
          ],
        },
      ],
    },
  });

  useEffect(() => {
    async function loadFeaturedMaterial() {
      try {
        const data = await fetchFeaturedMaterial();
        setFeaturedMaterial(data);
      } catch (err) {
        console.error('Failed to load featured material:', err);
      } finally {
        setLoading(false);
      }
    }

    loadFeaturedMaterial();
  }, []);

  // 新着の学習相談を取得
  useEffect(() => {
    async function loadRecentQuestions() {
      try {
        setQuestionsLoading(true);
        setQuestionsError(false);
        const data = await fetchRecentQuestions(5);
        setRecentQuestions(data);
      } catch (err) {
        console.error('Failed to load recent questions:', err);
        setQuestionsError(true);
      } finally {
        setQuestionsLoading(false);
      }
    }

    loadRecentQuestions();
  }, []);

  // 新着の居場所の悩みを取得
  useEffect(() => {
    async function loadRecentVoices() {
      try {
        setVoicesLoading(true);
        setVoicesError(false);
        const data = await fetchRecentVoices(5);
        setRecentVoices(data);
      } catch (err) {
        console.error('Failed to load recent voices:', err);
        setVoicesError(true);
      } finally {
        setVoicesLoading(false);
      }
    }

    loadRecentVoices();
  }, []);

  // 日付を相対表示に変換
  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return '今日';
    if (diffDays === 1) return '1日前';
    if (diffDays < 7) return `${diffDays}日前`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}週間前`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}ヶ月前`;
    return `${Math.floor(diffDays / 365)}年前`;
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/5"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <img
                  src="https://static.readdy.ai/image/8766ff501ca5aa7943b16e981fd86369/2d12e55e6c110384a1c215f2d66a17a3.png"
                  alt="キョウコレ - 学習支援教材プラットフォーム"
                  className="w-full h-auto rounded-2xl"
                />
                
                {/* ステッカー風バッジ：画像の左上に少し被せる */}
                <div
                  className={[
                    'absolute z-10',
                    // PC：左上に少し被せる（外側へはみ出し）
                    '-top-3 -left-3',
                    'sm:-top-4 sm:-left-4',
                    // モバイルは被りすぎないように（必要なら調整）
                    'max-sm:top-2 max-sm:left-2',
                    'scale-100 md:scale-110 origin-top-left',
                  ].join(' ')}
                >
                  <StickerSitesCountBadge
                    onClick={() => {
                      window.REACT_APP_NAVIGATE?.('/sites');
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Right Side - Text Content */}
            <div className="order-1 lg:order-2 text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight relative pl-6">
                <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#800019]"></span>
                無料で使える、<br />
                ちょうど良い教材、<br />
                コレクション。
              </h1>

              <p className="text-lg sm:text-xl text-gray-700 mb-8 leading-relaxed">
                教科書単元に合わせて教材を探したり、現場のアイデアや悩みの声を共有したりできる、先生・居場所スタッフのための教材情報サイトです。
              </p>
              <div className="flex flex-col gap-4 mb-6">
                <Link
                  to="/materials"
                  className="flex items-center px-6 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer group relative overflow-hidden"
                >
                  <i className="ri-book-open-line mr-2 text-xl"></i>
                  <div className="text-left flex-1">
                    <div className="font-semibold">単元で探す</div>
                    <div className="text-sm opacity-90">教科・学年・単元から教材を探す</div>
                  </div>
                </Link>
                <Link
                  to="/sites"
                  className="flex items-center px-6 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer group relative overflow-hidden"
                >
                  <i className="ri-global-line mr-2 text-xl"></i>
                  <div className="text-left flex-1">
                    <div className="font-semibold">サイトで探す</div>
                    <div className="text-sm opacity-90">無料教材サイトを一覧で比較する</div>
                  </div>
                </Link>
                <Link
                  to="/recipes"
                  className="flex items-center px-6 py-4 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer group relative overflow-hidden"
                >
                  <i className="ri-lightbulb-line mr-2 text-xl"></i>
                  <div className="text-left flex-1">
                    <div className="font-semibold">活用レシピを読む</div>
                    <div className="text-sm opacity-90">他の現場の使い方アイデアを見る</div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 教科別まとめページへの導線 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 relative inline-block">
            教科別に整理して探す
            <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-[#800019]"></span>
          </h2>
          <p className="text-gray-600 mb-6 pl-4 border-l-2 border-[#800019]">
            <span className="pl-2">各教科の学習内容を学年別・単元別に整理しました。つまずきやすいポイントや、おすすめの無料教材サイトもご紹介しています。</span>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/features/math-summary"
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 p-6 cursor-pointer group relative"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-2xl">
                  📐
                </div>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  数学
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                計算・関数・図形の整理と、つまずき単元の攻略法
              </p>
            </Link>
            <Link
              to="/features/english-summary"
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 p-6 cursor-pointer group relative"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-2xl">
                  🌍
                </div>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  英語
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                文法の整理順と、読解・リスニング対策
              </p>
            </Link>
            <Link
              to="/features/japanese-summary"
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 p-6 cursor-pointer group relative"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-2xl">
                  📖
                </div>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  国語
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                読解・文法・漢字の分け方と、読解の型
              </p>
            </Link>
            <Link
              to="/features/science-summary"
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 p-6 cursor-pointer group relative"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-2xl">
                  🔬
                </div>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  理科
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                物理・化学・生物・地学の分野別整理
              </p>
            </Link>
            <Link
              to="/features/social-summary"
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 p-6 cursor-pointer group relative"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center text-2xl">
                  🌏
                </div>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  社会
                </h3>
              </div>
              <p className="text-sm text-gray-600">
                地理・歴史・公民の流れと用語整理
              </p>
            </Link>
          </div>
        </section>

        {/* 今日のキョウコレ */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 relative inline-block">
            今日のキョウコレ
            <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-[#800019]"></span>
          </h2>
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
              <p className="mt-4 text-gray-600">読み込み中…</p>
            </div>
          ) : featuredMaterial ? (
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 p-6 md:p-8 relative">
              <div className="flex items-center gap-3 mb-4">
                {/* 教科アイコン */}
                {SUBJECT_ICON_MAP[featuredMaterial.subject] && (
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full text-xl ${SUBJECT_ICON_MAP[featuredMaterial.subject].bgClass}`}>
                    <span aria-hidden="true">{SUBJECT_ICON_MAP[featuredMaterial.subject].emoji}</span>
                  </div>
                )}
                <div className="flex flex-col">
                  <span className={`text-sm font-semibold ${SUBJECT_ICON_MAP[featuredMaterial.subject]?.textClass || 'text-gray-700'}`}>
                    {featuredMaterial.subject}
                  </span>
                  <span className="text-xs text-gray-500">
                    {getGradeRangeLabel(featuredMaterial.grade_min, featuredMaterial.grade_max)}
                  </span>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {featuredMaterial.title}
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {featuredMaterial.description.substring(0, 150)}
                {featuredMaterial.description.length > 150 ? '…' : ''}
              </p>
              <Link
                to={`/materials/${featuredMaterial.slug}`}
                className="inline-block px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap relative overflow-hidden group"
              >
                <span className="relative z-10">単元ガイドを見る</span>
                <span className="absolute inset-0 bg-[#800019] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 text-center">
              <p className="text-gray-600">注目の単元ガイドはまだありません。</p>
            </div>
          )}
        </section>

        {/* Boratasuへの導線 */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl shadow-sm border border-teal-100 p-6 md:p-8 relative">
            <div className="absolute top-0 left-8 w-0.5 h-full bg-[#800019] opacity-20"></div>
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-2xl relative">
                💡
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 relative inline-block">
                  キョウコレのレシピを、オンライン学習支援で実践しませんか？
                  <span className="absolute -bottom-1 left-0 w-16 h-0.5 bg-[#800019]"></span>
                </h2>
                <p className="text-gray-700 mb-4 leading-relaxed pl-4 border-l-2 border-[#800019]">
                  <span className="pl-2">キョウコレには、「こんなふうに教えてみたい」「こんな関わり方をしてみたい」といったアイデアが集まっています。
                  オンライン学習支援プラットフォーム「Boratasu（ボラタス）」を使うと、
                  そのアイデアを、ボランティアと一緒にオンラインで実践することができます。</span>
                </p>
              </div>
            </div>
            <ul className="space-y-2 mb-6 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>
                  <strong>居場所や学校：</strong>人手不足でも、外部のオンラインボランティアとつながって学習支援を増やせる
                </span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-teal-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>
                  <strong>ボランティア：</strong>自分の得意や経験をいかして、全国の子どもたちの学びをサポートできる
                </span>
              </li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://boratasu.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap relative overflow-hidden group"
              >
                <span className="relative z-10">
                  居場所・学校の方へ（Boratasuを見る）
                  <i className="ri-external-link-line ml-2"></i>
                </span>
                <span className="absolute inset-0 bg-[#800019] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
              </a>
              <a
                href="https://zion-craft.work/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-teal-600 font-medium rounded-lg border-2 border-teal-600 hover:bg-teal-50 transition-colors duration-200 cursor-pointer whitespace-nowrap relative group"
              >
                <span className="relative z-10">
                  ボランティアの方へ（ボラタスについて詳しく知る）
                  <i className="ri-external-link-line ml-2"></i>
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* 単元から探す */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 relative inline-block">
            単元から探す
            <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-[#800019]"></span>
          </h2>
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 md:p-8 text-center relative">
            <div className="inline-block w-16 h-16 bg-gradient-to-br from-teal-100 to-blue-100 rounded-full flex items-center justify-center text-3xl mb-4 relative">
              🔍
            </div>
            <p className="text-gray-700 mb-6">
              学年や教科から、ぴったりの単元ガイドを見つけましょう。
            </p>
            <Link
              to="/materials"
              className="inline-block px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap relative overflow-hidden group"
            >
              <span className="relative z-10">単元ガイド一覧を見る</span>
              <span className="absolute inset-0 bg-[#800019] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
            </Link>
          </div>
        </section>

        {/* 新着の学習相談 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 relative inline-block">
            新着の学習相談
            <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-[#800019]"></span>
          </h2>
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 md:p-8 relative">
            {questionsLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <p className="mt-4 text-gray-600">読み込み中…</p>
              </div>
            ) : questionsError ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">データの読み込みに失敗しました。</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  再読み込み
                </button>
              </div>
            ) : recentQuestions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">まだ投稿がありません。</p>
              </div>
            ) : (
              <>
                <ul className="space-y-4 mb-6">
                  {recentQuestions.map((question, index) => (
                    <li key={question.id} className={index < recentQuestions.length - 1 ? 'border-b border-gray-200 pb-3' : 'pb-3'}>
                      <Link 
                        to={`/questions/${question.slug || question.id}`} 
                        className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                      >
                        {question.title}
                      </Link>
                      <p className="text-sm text-gray-500 mt-1">
                        {question.subject && question.grade ? `${question.grade} ${question.subject}` : ''}
                        {question.subject && question.grade && question.updated_at ? ' • ' : ''}
                        {question.updated_at ? formatRelativeDate(question.updated_at) : ''}
                      </p>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/questions"
                  className="inline-block px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap relative overflow-hidden group"
                >
                  <span className="relative z-10">学習相談一覧を見る</span>
                  <span className="absolute inset-0 bg-[#800019] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                </Link>
              </>
            )}
          </div>
        </section>

        {/* 新着の居場所の悩み */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 relative inline-block">
            新着の居場所の悩み
            <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-[#800019]"></span>
          </h2>
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 md:p-8 relative">
            {voicesLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <p className="mt-4 text-gray-600">読み込み中…</p>
              </div>
            ) : voicesError ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">データの読み込みに失敗しました。</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  再読み込み
                </button>
              </div>
            ) : recentVoices.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">まだ投稿がありません。</p>
              </div>
            ) : (
              <>
                <ul className="space-y-4 mb-6">
                  {recentVoices.map((voice, index) => (
                    <li key={voice.id} className={index < recentVoices.length - 1 ? 'border-b border-gray-200 pb-3' : 'pb-3'}>
                      <Link 
                        to={`/voices/${voice.slug || voice.id}`} 
                        className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                      >
                        {voice.title}
                      </Link>
                      <p className="text-sm text-gray-500 mt-1">
                        {voice.category ? voice.category : ''}
                        {voice.category && voice.updated_at ? ' • ' : ''}
                        {voice.updated_at ? formatRelativeDate(voice.updated_at) : ''}
                      </p>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/voices"
                  className="inline-block px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap relative overflow-hidden group"
                >
                  <span className="relative z-10">居場所の悩み一覧を見る</span>
                  <span className="absolute inset-0 bg-[#800019] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                </Link>
              </>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default HomePage;
